/**
 * Per-endpoint circuit breaker (in-memory).
 *
 * States:
 *   CLOSED    — normal operation, all calls pass through.
 *   OPEN      — after `threshold` consecutive failures, all calls fail-fast
 *               for `timeoutMs`. Prevents retry-storms.
 *   HALF_OPEN — after the timeout expires, ONE call is allowed through. If
 *               it succeeds the breaker closes; if it fails the breaker
 *               re-opens for another `timeoutMs`.
 *
 * The breaker is keyed by URL so a broken endpoint does not disable the
 * whole CMS layer.
 */
import { emit, CMS_EVENTS } from "./cmsTelemetry";

export const STATE = Object.freeze({
  CLOSED:    "closed",
  OPEN:      "open",
  HALF_OPEN: "half-open",
});

const registry = new Map();

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

function getEntry(url) {
  let e = registry.get(url);
  if (!e) {
    e = { state: STATE.CLOSED, failures: 0, openedAt: 0 };
    registry.set(url, e);
  }
  return e;
}

/**
 * Ask the breaker whether we may proceed with a request.
 * @returns {boolean} true → proceed. false → fail-fast (breaker OPEN).
 */
export function allow(url, config) {
  const e = getEntry(url);
  if (e.state === STATE.CLOSED) return true;
  if (e.state === STATE.OPEN) {
    if (now() - e.openedAt >= config.breakerTimeoutMs) {
      e.state = STATE.HALF_OPEN;
      emit(CMS_EVENTS.BREAKER_HALF_OPEN, { url });
      return true; // allow ONE probe
    }
    emit(CMS_EVENTS.BREAKER_TRIP, { url });
    return false;
  }
  // HALF_OPEN — allow exactly one request. Additional in-flight requests
  // to the same URL are rare (React StrictMode aside) and even if they
  // slip through the fallback path renders the site correctly.
  return true;
}

/** Report a successful call. Resets counter, closes the breaker. */
export function recordSuccess(url) {
  const e = getEntry(url);
  const wasOpen = e.state !== STATE.CLOSED;
  e.state = STATE.CLOSED;
  e.failures = 0;
  e.openedAt = 0;
  if (wasOpen) emit(CMS_EVENTS.BREAKER_CLOSE, { url });
}

/** Report a failed call. Opens the breaker at the configured threshold. */
export function recordFailure(url, config) {
  const e = getEntry(url);
  e.failures += 1;
  if (e.state === STATE.HALF_OPEN || e.failures >= config.breakerThreshold) {
    if (e.state !== STATE.OPEN) {
      e.state = STATE.OPEN;
      e.openedAt = now();
      emit(CMS_EVENTS.BREAKER_OPEN, {
        url,
        consecutiveFailures: e.failures,
      });
    } else {
      // Already OPEN — refresh the openedAt so timer restarts.
      e.openedAt = now();
    }
  }
}

/** Introspection helper — useful for tests and health dashboards. */
export function snapshot() {
  const out = {};
  registry.forEach((v, k) => { out[k] = { ...v }; });
  return out;
}

/** Reset all breakers (used only in tests). */
export function _resetAll() {
  registry.clear();
}
